import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Download,
  Info,
  X,
  Share,
  PlusSquare,
  Apple,
  Copy,
  ChevronUp,
  Compass,
} from "lucide-react";
import { toast } from "sonner";

import { api } from "../api";
import { t } from "../i18n";

/** Smoothly tweens from previous value to target (ease-out cubic). */
function useCountUp(target, durationMs = 900) {
  const [val, setVal] = useState(target);
  const fromRef = useRef(target);
  useEffect(() => {
    const from = fromRef.current;
    const to = target;
    if (from === to) return;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / durationMs);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(from + (to - from) * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
      else fromRef.current = to;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs]);
  return val;
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  // iPadOS 13+ reports as Mac in UA; check touch + platform too.
  return /iPhone|iPad|iPod/i.test(ua) ||
    (ua.includes("Mac") && typeof document !== "undefined" && "ontouchend" in document);
}

/**
 * Returns the iOS browser kind:
 *   "safari" | "chrome" | "firefox" | "edge" | "opera"
 *   | "instagram" | "facebook" | "whatsapp" | "line" | "twitter"
 *   | "snapchat" | "tiktok" | "in-app" (generic webview) | "other" (not iOS)
 */
function getIOSBrowserKind() {
  if (typeof navigator === "undefined") return "other";
  if (!isIOS()) return "other";
  const ua = navigator.userAgent || "";
  if (/CriOS/i.test(ua)) return "chrome";
  if (/FxiOS/i.test(ua)) return "firefox";
  if (/EdgiOS/i.test(ua)) return "edge";
  if (/OPiOS|OPT\//i.test(ua)) return "opera";
  if (/Instagram/i.test(ua)) return "instagram";
  if (/FBAN|FBAV|FB_IAB|FBIOS/i.test(ua)) return "facebook";
  if (/WhatsApp/i.test(ua)) return "whatsapp";
  if (/Line\//i.test(ua)) return "line";
  if (/Twitter/i.test(ua)) return "twitter";
  if (/Snapchat/i.test(ua)) return "snapchat";
  if (/Bytedance|musical_ly|TikTok/i.test(ua)) return "tiktok";
  // Real Safari on iOS contains "Safari/" and "Version/". In-app WKWebViews
  // typically omit "Safari/".
  if (/Safari\//.test(ua) && /Version\//.test(ua)) return "safari";
  return "in-app";
}

const IOS_BROWSER_LABELS = {
  chrome: "Chrome",
  firefox: "Firefox",
  edge: "Edge",
  opera: "Opera",
  instagram: "Instagram",
  facebook: "Facebook",
  whatsapp: "WhatsApp",
  line: "LINE",
  twitter: "X / Twitter",
  snapchat: "Snapchat",
  tiktok: "TikTok",
  "in-app": "this app's browser",
};

/** Detect Chromium-family browsers that support beforeinstallprompt. */
function isChromiumBrowser() {
  if (typeof navigator === "undefined") return false;
  if (isIOS()) return false;
  const ua = navigator.userAgent || "";
  return /Chrom(e|ium)|Edg\/|SamsungBrowser|OPR\//.test(ua);
}

/* ------------------------------------------------------------------ */
/*  Visual mockups (pure CSS/SVG) used inside the iOS install guide   */
/* ------------------------------------------------------------------ */

/** Phone frame wrapper. Children render inside the screen area. */
function PhoneFrame({ children, label }) {
  return (
    <div className="relative">
      <div className="relative w-full max-w-[150px] aspect-[3/4] rounded-[24px] bg-slate-900 p-1.5 shadow-[0_8px_0_rgba(0,0,0,0.18)] mx-auto">
        <div className="relative w-full h-full rounded-[18px] bg-white overflow-hidden">
          {/* iPhone notch */}
          <div className="absolute top-1 left-1/2 -translate-x-1/2 w-12 h-2.5 rounded-full bg-slate-900 z-10" />
          {children}
        </div>
      </div>
      {label && (
        <p className="mt-1.5 text-center text-[10px] font-bold uppercase tracking-wide text-slate-500 leading-tight">
          {label}
        </p>
      )}
    </div>
  );
}

/** Step 1 visual: Safari tab with the Share button highlighted. */
function PhoneMockupShare() {
  return (
    <PhoneFrame label="Step 1 · Tap Share">
      {/* Safari URL bar */}
      <div className="absolute top-7 left-2 right-2 h-7 rounded-lg bg-slate-100 flex items-center px-2 gap-1.5">
        <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
        <span className="text-[9px] font-bold text-slate-500 truncate">saral-ai.com</span>
      </div>

      {/* Page placeholder content */}
      <div className="absolute top-[60px] left-3 right-3 space-y-1.5">
        <div className="h-2 rounded bg-pink-100 w-3/4" />
        <div className="h-2 rounded bg-slate-100 w-full" />
        <div className="h-2 rounded bg-slate-100 w-5/6" />
        <div className="mt-2 h-16 rounded-lg bg-gradient-to-br from-pink-200 via-violet-200 to-amber-200" />
      </div>

      {/* Safari bottom toolbar */}
      <div className="absolute bottom-0 left-0 right-0 h-12 bg-slate-50 border-t border-slate-200 flex items-center justify-around px-2">
        <ChevronUp size={14} className="text-slate-300" strokeWidth={2.5} />
        <ChevronUp size={14} className="text-slate-300 rotate-180" strokeWidth={2.5} />
        {/* The Share button — highlighted */}
        <motion.div
          animate={{ scale: [1, 1.18, 1] }}
          transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
          className="relative w-9 h-9 rounded-lg bg-sky-100 ring-2 ring-sky-400 flex items-center justify-center shadow-[0_0_0_4px_rgba(56,189,248,0.25)]"
        >
          <Share size={18} strokeWidth={2.75} className="text-sky-600" />
        </motion.div>
        <div className="w-5 h-3 rounded bg-slate-200" />
        <div className="w-5 h-3 rounded bg-slate-200" />
      </div>

      {/* Arrow pointing to share */}
      <motion.div
        animate={{ y: [0, -4, 0] }}
        transition={{ duration: 1.1, repeat: Infinity, ease: "easeInOut" }}
        className="absolute bottom-12 left-1/2 -translate-x-1/2 text-sky-500 font-black text-2xl leading-none"
        aria-hidden
      >
        ▼
      </motion.div>
    </PhoneFrame>
  );
}

/** Step 2 visual: Share sheet with Add to Home Screen row highlighted. */
function PhoneMockupAddToHome() {
  return (
    <PhoneFrame label="Step 2 · Add to Home Screen">
      {/* Dim background */}
      <div className="absolute inset-0 bg-slate-900/30" />

      {/* Share sheet */}
      <div className="absolute left-2 right-2 bottom-2 rounded-2xl bg-white shadow-2xl overflow-hidden">
        <div className="px-2.5 pt-2.5 pb-1 text-[9px] font-bold text-slate-400 uppercase tracking-wide">
          Share
        </div>
        {/* App row */}
        <div className="px-2 pb-2 flex gap-1.5 overflow-hidden">
          <div className="w-7 h-7 rounded-lg bg-emerald-200" />
          <div className="w-7 h-7 rounded-lg bg-amber-200" />
          <div className="w-7 h-7 rounded-lg bg-rose-200" />
          <div className="w-7 h-7 rounded-lg bg-sky-200" />
        </div>
        <div className="border-t border-slate-100 divide-y divide-slate-100">
          <div className="flex items-center justify-between px-2.5 py-1.5 text-[10px] font-semibold text-slate-700">
            <span>Copy</span>
            <div className="w-3.5 h-3.5 rounded-sm bg-slate-200" />
          </div>
          {/* Highlighted row */}
          <motion.div
            animate={{ backgroundColor: ["#ecfeff", "#bae6fd", "#ecfeff"] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
            className="flex items-center justify-between px-2.5 py-2 ring-2 ring-emerald-400 rounded-md mx-1 my-1"
          >
            <span className="text-[11px] font-bold text-emerald-700">Add to Home Screen</span>
            <PlusSquare size={14} strokeWidth={2.75} className="text-emerald-600" />
          </motion.div>
          <div className="flex items-center justify-between px-2.5 py-1.5 text-[10px] font-semibold text-slate-700">
            <span>Markup</span>
            <div className="w-3.5 h-3.5 rounded-sm bg-slate-200" />
          </div>
        </div>
      </div>
    </PhoneFrame>
  );
}

/* ------------------------------------------------------------------ */
/*  Modal: iOS Safari install instructions                             */
/* ------------------------------------------------------------------ */

function IOSInstructionsModal({ onClose, onInstalled }) {
  return (
    <motion.div
      data-testid="ios-install-modal"
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm px-3 pb-4 sm:pb-3"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 60, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-white rounded-3xl p-5 border-4 border-white shadow-[0_12px_0_rgba(0,0,0,0.12)] max-h-[92vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 font-heading text-xl font-bold text-slate-800">
            <Apple size={24} strokeWidth={2.5} className="text-fuchsia-600" />
            Install on iPhone
          </div>
          <button
            data-testid="ios-modal-close"
            onClick={onClose}
            className="w-10 h-10 rounded-full bg-slate-100 active:bg-slate-200 flex items-center justify-center"
            aria-label="Close"
          >
            <X size={20} strokeWidth={2.5} className="text-slate-600" />
          </button>
        </div>

        {/* Visual demo: two phone mockups side-by-side */}
        <div className="mt-5 grid grid-cols-2 gap-3">
          <PhoneMockupShare />
          <PhoneMockupAddToHome />
        </div>

        {/* 3 concise steps with BIG icons */}
        <ol className="mt-6 space-y-4">
          <li className="flex items-center gap-3">
            <span className="shrink-0 w-9 h-9 rounded-full bg-pink-500 text-white font-black flex items-center justify-center text-base shadow-[0_3px_0_rgba(0,0,0,0.12)]">
              1
            </span>
            <div className="flex items-center gap-2 text-base font-bold text-slate-800">
              Tap
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-sky-100 border-2 border-sky-300">
                <Share size={22} strokeWidth={2.75} className="text-sky-600" />
              </span>
            </div>
          </li>
          <li className="flex items-center gap-3">
            <span className="shrink-0 w-9 h-9 rounded-full bg-pink-500 text-white font-black flex items-center justify-center text-base shadow-[0_3px_0_rgba(0,0,0,0.12)]">
              2
            </span>
            <div className="flex items-center gap-2 text-base font-bold text-slate-800">
              Tap
              <span className="inline-flex items-center gap-1.5 px-2.5 h-10 rounded-xl bg-emerald-100 border-2 border-emerald-300">
                <PlusSquare size={20} strokeWidth={2.75} className="text-emerald-700" />
                <span className="text-emerald-800 text-sm font-extrabold">Add to Home Screen</span>
              </span>
            </div>
          </li>
          <li className="flex items-center gap-3">
            <span className="shrink-0 w-9 h-9 rounded-full bg-pink-500 text-white font-black flex items-center justify-center text-base shadow-[0_3px_0_rgba(0,0,0,0.12)]">
              3
            </span>
            <div className="flex items-center gap-2 text-base font-bold text-slate-800">
              Tap
              <span className="inline-flex items-center px-3 h-10 rounded-xl bg-amber-100 border-2 border-amber-300 text-amber-800 font-extrabold">
                Add
              </span>
            </div>
          </li>
        </ol>

        <button
          data-testid="ios-modal-done"
          onClick={onInstalled}
          className="mt-6 w-full bg-emerald-500 hover:bg-emerald-600 text-white font-heading font-extrabold uppercase text-base tracking-wide py-4 rounded-2xl active:translate-y-0.5 transition shadow-[0_4px_0_rgba(0,0,0,0.14)]"
        >
          I've installed it
        </button>
      </motion.div>
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/*  Modal: "Open in Safari" prompt for non-Safari iOS browsers         */
/* ------------------------------------------------------------------ */

function OpenInSafariModal({ onClose, browserLabel }) {
  const [copied, setCopied] = useState(false);
  const url =
    typeof window !== "undefined" ? window.location.href : "https://saral-ai.com";

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied — now paste in Safari");
      setTimeout(() => setCopied(false), 2500);
    } catch (_e) {
      toast.error("Couldn't copy. Long-press the address bar and copy manually.");
    }
  };

  return (
    <motion.div
      data-testid="open-in-safari-modal"
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm px-3 pb-4 sm:pb-3"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 60, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-white rounded-3xl p-5 border-4 border-white shadow-[0_12px_0_rgba(0,0,0,0.12)]"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 font-heading text-xl font-bold text-slate-800">
            <Compass size={24} strokeWidth={2.5} className="text-sky-500" />
            Open in Safari
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full bg-slate-100 active:bg-slate-200 flex items-center justify-center"
            aria-label="Close"
          >
            <X size={20} strokeWidth={2.5} className="text-slate-600" />
          </button>
        </div>

        {/* Big visual: Safari compass icon */}
        <div className="mt-5 flex flex-col items-center">
          <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-sky-400 to-blue-600 flex items-center justify-center shadow-[0_8px_0_rgba(0,0,0,0.15)]">
            <Compass size={56} strokeWidth={2.25} className="text-white" />
          </div>
          <p className="mt-4 text-center text-lg font-extrabold text-slate-800 leading-snug">
            Please open this page in Safari to install Saral Eye.
          </p>
          <p className="mt-2 text-center text-sm font-semibold text-slate-500 leading-snug">
            You're using <b>{browserLabel}</b>. Only Safari can add Saral Eye to your home screen on iPhone.
          </p>
        </div>

        {/* Simple steps */}
        <ol className="mt-5 space-y-2.5">
          <li className="flex items-center gap-3">
            <span className="shrink-0 w-8 h-8 rounded-full bg-sky-500 text-white font-black flex items-center justify-center text-sm shadow-[0_3px_0_rgba(0,0,0,0.12)]">1</span>
            <span className="text-base font-bold text-slate-800">Copy the link below.</span>
          </li>
          <li className="flex items-center gap-3">
            <span className="shrink-0 w-8 h-8 rounded-full bg-sky-500 text-white font-black flex items-center justify-center text-sm shadow-[0_3px_0_rgba(0,0,0,0.12)]">2</span>
            <span className="text-base font-bold text-slate-800">Open the <b>Safari</b> app.</span>
          </li>
          <li className="flex items-center gap-3">
            <span className="shrink-0 w-8 h-8 rounded-full bg-sky-500 text-white font-black flex items-center justify-center text-sm shadow-[0_3px_0_rgba(0,0,0,0.12)]">3</span>
            <span className="text-base font-bold text-slate-800">Paste the link and tap Go.</span>
          </li>
        </ol>

        <button
          data-testid="copy-link-btn"
          onClick={handleCopy}
          className="mt-5 w-full bg-sky-500 hover:bg-sky-600 text-white font-heading font-extrabold uppercase text-base tracking-wide py-4 rounded-2xl active:translate-y-0.5 transition shadow-[0_4px_0_rgba(0,0,0,0.14)] flex items-center justify-center gap-2"
        >
          <Copy size={20} strokeWidth={3} />
          {copied ? "Link copied!" : "Copy link"}
        </button>

        <div className="mt-3 p-3 rounded-2xl bg-amber-50 border-2 border-amber-200 text-[13px] text-amber-800 font-bold leading-snug flex items-start gap-2">
          <Info size={16} strokeWidth={2.75} className="text-amber-600 mt-0.5 shrink-0" />
          Chrome, Instagram, WhatsApp & Facebook browsers can't install apps on iPhone — only Safari can.
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main card                                                          */
/* ------------------------------------------------------------------ */

export default function InstallAppCard({ lang }) {
  const [count, setCount] = useState(0);
  const [installed, setInstalled] = useState(isStandalone());
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showIosModal, setShowIosModal] = useState(false);
  const [showSafariPrompt, setShowSafariPrompt] = useState(false);
  const [supportsChromium] = useState(isChromiumBrowser());
  const [supportsIOS] = useState(isIOS());
  const [iosBrowser] = useState(getIOSBrowserKind());
  const animatedCount = useCountUp(count);

  const iosIsSafari = iosBrowser === "safari";
  const iosNeedsSafariSwitch = supportsIOS && !iosIsSafari;
  const iosBrowserLabel = IOS_BROWSER_LABELS[iosBrowser] || "your current browser";

  const fetchCount = async () => {
    try {
      const r = await api.get("/installs/count");
      setCount(Number(r.data?.count) || 0);
    } catch (_e) { /* silent */ }
  };

  const recordInstallOnce = async () => {
    if (sessionStorage.getItem("saraleye_install_counted")) return;
    sessionStorage.setItem("saraleye_install_counted", "1");
    try {
      const r = await api.post("/installs/increment");
      setCount(Number(r.data?.count) || 0);
    } catch (_e) {
      setCount((c) => c + 1);
    }
  };

  useEffect(() => {
    fetchCount();

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = async () => {
      setInstalled(true);
      setDeferredPrompt(null);
      await recordInstallOnce();
      toast.success(t(lang, "install_success") || "App installed!");
    };
    const onDisplayModeChange = (e) => { if (e.matches) setInstalled(true); };

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    const mq = window.matchMedia?.("(display-mode: standalone)");
    mq?.addEventListener?.("change", onDisplayModeChange);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      mq?.removeEventListener?.("change", onDisplayModeChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (installed) return null;

  const handleInstall = async (e) => {
    e.stopPropagation();
    if (busy) return;

    if (supportsIOS) {
      // Non-Safari iOS browser → strong "open in Safari" prompt.
      if (!iosIsSafari) {
        setShowSafariPrompt(true);
        return;
      }
      setShowIosModal(true);
      return;
    }

    if (!deferredPrompt) {
      toast.message(
        "Tap browser menu (⋮) → 'Install app' or 'Add to Home screen'.",
        { duration: 5000 }
      );
      return;
    }

    setBusy(true);
    try {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      setDeferredPrompt(null);
    } finally {
      setBusy(false);
    }
  };

  const handleIosInstalled = async () => {
    setShowIosModal(false);
    await recordInstallOnce();
    toast.success("Thanks! Open Saral Eye from your home screen now.");
  };

  const ctaSupported = supportsChromium || supportsIOS;

  return (
    <>
      {/* STRONG banner for non-Safari iOS users — sits above the install card. */}
      {iosNeedsSafariSwitch && (
        <motion.div
          data-testid="open-in-safari-banner"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-5 rounded-2xl border-4 border-white shadow-[0_6px_0_rgba(0,0,0,0.06)] overflow-hidden"
          style={{ background: "linear-gradient(135deg,#0ea5e9 0%,#2563eb 100%)" }}
        >
          <button
            data-testid="open-in-safari-banner-btn"
            onClick={() => setShowSafariPrompt(true)}
            className="w-full flex items-center gap-3 p-3.5 text-left active:translate-y-0.5 transition"
          >
            <div className="shrink-0 w-12 h-12 rounded-2xl bg-white/95 flex items-center justify-center shadow-[0_3px_0_rgba(0,0,0,0.12)]">
              <Compass size={26} strokeWidth={2.5} className="text-sky-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-heading text-white text-base font-extrabold leading-tight">
                Please open this page in Safari to install Saral Eye.
              </div>
              <div className="text-white/90 text-[12px] font-semibold mt-0.5">
                Tap to see how · You're using {iosBrowserLabel}
              </div>
            </div>
          </button>
        </motion.div>
      )}

      <motion.div
        data-testid="install-app-card"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mt-5 relative overflow-hidden rounded-2xl border-4 border-white shadow-[0_6px_0_rgba(0,0,0,0.06)]"
        style={{ background: "linear-gradient(135deg,#a78bfa 0%,#f472b6 55%,#fb923c 100%)" }}
      >
        <span aria-hidden className="pointer-events-none absolute -top-6 -right-4 w-20 h-20 rounded-full bg-white/20 blur-xl" />

        <div className="relative flex items-center gap-3 pl-4 pr-2 py-2">
          <div className="flex-1 min-w-0">
            <div
              data-testid="install-counter"
              className="font-heading text-white text-lg sm:text-xl font-bold leading-none drop-shadow-sm"
            >
              <span className="tabular-nums">{animatedCount.toLocaleString()}+</span>
              <span className="ml-1.5">{t(lang, "app_installs")}</span>
            </div>
            <p className="text-white/85 text-[11px] font-semibold mt-0.5">
              {t(lang, "platform_name")} · {supportsIOS ? "iOS PWA" : "PWA"}
            </p>
          </div>

          {ctaSupported ? (
            <button
              data-testid="install-app-btn"
              onClick={handleInstall}
              disabled={busy}
              className="relative shrink-0 bg-white/95 hover:bg-white text-slate-800 font-heading text-sm font-bold uppercase tracking-wide px-3.5 py-2.5 rounded-xl flex items-center gap-1.5 active:translate-y-0.5 transition disabled:opacity-80 shadow-[0_3px_0_rgba(0,0,0,0.12)]"
            >
              {supportsIOS ? (
                <>
                  <Apple size={16} strokeWidth={3} className="text-fuchsia-600" />
                  {busy ? "..." : t(lang, "install_app")}
                </>
              ) : (
                <>
                  <Download size={16} strokeWidth={3} className="text-fuchsia-600" />
                  {busy ? "..." : t(lang, "install_app")}
                </>
              )}
            </button>
          ) : (
            <div
              data-testid="install-app-fallback-badge"
              className="shrink-0 bg-white/95 text-slate-800 text-[11px] font-bold uppercase tracking-wide px-3 py-2 rounded-xl flex items-center gap-1.5"
            >
              <Info size={14} strokeWidth={3} className="text-fuchsia-600" />
              PWA
            </div>
          )}
        </div>

        {!ctaSupported && (
          <div
            data-testid="install-app-fallback-message"
            className="relative bg-white/95 text-slate-700 text-[12px] sm:text-xs font-semibold px-4 py-2.5 leading-snug border-t-2 border-white/60 flex items-start gap-2"
          >
            <Info size={14} strokeWidth={2.75} className="text-fuchsia-600 mt-px shrink-0" />
            Open this website in Chrome (Android) or Safari (iPhone) to install the app.
          </div>
        )}
      </motion.div>

      <AnimatePresence>
        {showIosModal && (
          <IOSInstructionsModal
            onClose={() => setShowIosModal(false)}
            onInstalled={handleIosInstalled}
          />
        )}
        {showSafariPrompt && (
          <OpenInSafariModal
            onClose={() => setShowSafariPrompt(false)}
            browserLabel={iosBrowserLabel}
          />
        )}
      </AnimatePresence>
    </>
  );
}
